// Map GraphQL response shapes onto the UI's domain types.

import type {
  AccountEntity,
  AddressEntity,
  AssetEntity,
  BusinessEntity,
  CarEntity,
  CarRentalEntity,
  CaseEntity,
  CasePriority,
  CaseStatus,
  ContactEntity,
  Entity,
  GenericEntity,
  GenericKind,
  NoteEntity,
  TaskEntity,
  TrackingEntity,
} from "../types";
import type {
  GqlAddress,
  GqlAsset,
  GqlBusiness,
  GqlBusinessAccount,
  GqlCar,
  GqlCarRental,
  GqlCase,
  GqlContact,
  GqlContactResolution,
  GqlNote,
  GqlTask,
  GqlTrackingNumber,
  Linkable,
} from "./types";

const PALETTES: Array<"a" | "b" | "c" | "d"> = ["a", "b", "c", "d"];

function paletteFor(seed: string): "a" | "b" | "c" | "d" {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return PALETTES[Math.abs(h) % PALETTES.length];
}

function initialsFor(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function displayNameOf(c: GqlContact): string {
  return (
    c.display_name ||
    [c.firstname, c.lastname].filter(Boolean).join(" ").trim() ||
    c.email_1 ||
    c.email_2 ||
    c.email_3 ||
    "(unknown)"
  );
}

/** Build a ContactEntity from a GraphQL Contact record. */
export function contactFromGql(gql: GqlContact, matchedEmail?: string): ContactEntity {
  const display = displayNameOf(gql);
  const email = matchedEmail ?? gql.email_1 ?? gql.email_2 ?? gql.email_3 ?? "";
  return {
    id: gql.id,
    kind: "contact",
    title: gql.record_title,
    email,
    inEpic: true,
    ref: gql.reference_number,
    role: gql.job_title ?? undefined,
    company: gql.business?.name ?? undefined,
    initials: initialsFor(display),
    palette: paletteFor(gql.id),
    rating: gql.rating,
    headshotDriveId:
      gql.has_headshot && gql.headshot_sharepoint_drive_id
        ? gql.headshot_sharepoint_drive_id
        : undefined,
    headshotItemId:
      gql.has_headshot && gql.headshot_sharepoint_item_id
        ? gql.headshot_sharepoint_item_id
        : undefined,
    emoji: gql.emoji,
  };
}

/**
 * Apply a ContactResolution to a base ContactEntity (one we built from
 * Office.js). When matched, replaces basic fields with full EPIC data and
 * marks inEpic true. Otherwise leaves base mostly intact (inEpic = false).
 */
export function applyResolution(
  base: ContactEntity,
  res: GqlContactResolution | undefined
): ContactEntity {
  if (!res) return base;
  if (res.status === "CONTACT_MATCH" && res.contact) {
    return contactFromGql(res.contact, base.email);
  }
  // DOMAIN_MATCH / SHARED_MAILBOX / NO_MATCH — keep base, just confirm not in EPIC
  return { ...base, inEpic: false, ref: undefined };
}

// ── Linkable → UI Entity ───────────────────────────────────────────────

export function entityFromLinkable(l: Linkable): Entity | null {
  switch (l.__typename) {
    case "Contact":
      return contactFromGql(l);
    case "Business":
      return businessFromGql(l);
    case "BusinessAccount":
      return accountFromGql(l);
    case "Case":
      return caseFromGql(l);
    case "Task":
      return taskFromGql(l);
    case "Note":
      return noteFromGql(l);
    case "Address":
      return addressFromGql(l);
    case "Car":
      return carFromGql(l);
    case "CarRental":
      return carRentalFromGql(l);
    case "Asset":
      return assetFromGql(l);
    case "TrackingNumber":
      return trackingFromGql(l);
    case "Trip":
      return genericFromGql(l, "trip");
    case "Correspondence":
      return genericFromGql(l, "correspondence");
    case "PhoneCall":
      return genericFromGql(l, "phone_call");
    case "SmsMessage":
      return genericFromGql(l, "sms");
    case "FinanceCard":
      return genericFromGql(l, "finance_card");
    case "FinanceCreditline":
      return genericFromGql(l, "finance_creditline");
    case "Aircraft":
      return genericFromGql(l, "aircraft");
    case "LegalEntity":
      return genericFromGql(l, "legal_entity");
    case "Employment":
      return genericFromGql(l, "employment");
    case "Tag":
      return genericFromGql(l, "tag");
    case "PassengerNameRecord":
      return genericFromGql(l, "pnr");
    case "ExchangeCalendarEvent":
      return genericFromGql(l, "calendar_event");
    case "MedicalEncounter":
      return genericFromGql(l, "medical_encounter");
    case "Email":
      // The UI's Entity union doesn't include Email. Emails-as-evidence
      // are normally the OTHER side of the connection, not what we display.
      return null;
    default:
      // Catch-all for any Linkable member without a rich mapper above —
      // every connectable model exposes the universal HasModelConnections
      // fields (id / record_title / emoji), so as long as the query fetched
      // them (see LINKABLE_FRAGMENTS), we render the row generically instead
      // of dropping it. This is what makes new backend entity types show up
      // without a per-type mapper here; once the backend DisplayableRecord
      // interface lands (epic-laravel#50) even the query stops needing
      // per-type fragments. `l` is `never` to TS here because the wire union
      // has more members than our hand-written `Linkable` type enumerates.
      return genericFromDisplayable(l as unknown as DisplayableShape);
  }
}

/** Minimal universal shape shared by every HasModelConnections model. */
type DisplayableShape = {
  __typename?: string;
  id?: string;
  record_title?: string;
  emoji?: string;
  reference_number?: string;
};

/**
 * Render any Linkable member generically from its universal fields. Returns
 * null only if the fields weren't fetched (e.g. an un-fragmented future type
 * that came back as bare __typename) — in which case the row is dropped, same
 * as before. The human type label is derived from __typename so the detail
 * view still reads "Travel credit" rather than a generic bucket name.
 */
function genericFromDisplayable(g: DisplayableShape): GenericEntity | null {
  if (!g.id || !g.record_title) return null;
  return {
    id: g.id,
    kind: "record",
    title: g.record_title,
    ref: g.reference_number,
    emoji: g.emoji,
    typeLabel: g.__typename ? humanizeTypename(g.__typename) : undefined,
    typename: g.__typename,
  };
}

/** "TravelCredit" → "Travel credit", "PhoneNumber" → "Phone number". */
function humanizeTypename(typename: string): string {
  const spaced = typename
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Build a GenericEntity from any minimal-shape Linkable. These types
 * share the same surface (record_title / emoji / optional ref) — they
 * render uniformly in EntityRow.
 */
function genericFromGql(
  g: { id: string; record_title: string; emoji: string; reference_number?: string },
  kind: GenericKind
): GenericEntity {
  return {
    id: g.id,
    kind,
    title: g.record_title,
    ref: g.reference_number,
    emoji: g.emoji,
  };
}

function businessFromGql(b: GqlBusiness): BusinessEntity {
  return {
    id: b.id,
    kind: "business",
    title: b.record_title,
    ref: b.reference_number,
    meta: undefined,
    emoji: b.emoji,
  };
}

function accountFromGql(a: GqlBusinessAccount): AccountEntity {
  // Show the parent business when we have it; else fall back to account
  // number so the row has a useful secondary line.
  const meta =
    a.business?.name ?? a.account_number ?? undefined;
  return {
    id: a.id,
    kind: "account",
    title: a.record_title,
    ref: a.reference_number,
    meta,
    emoji: a.emoji,
    accountNumber: a.account_number ?? undefined,
    currency: a.currency_code ?? undefined,
  };
}

function caseFromGql(c: GqlCase): CaseEntity {
  // Backend CaseStatus / CasePriority enums are PascalCase. Normalize to
  // the UI's lowercase variants — straightforward 1:1 mapping, no
  // synthesis between status and priority (they're different concepts).
  const status: CaseStatus =
    c.status === "Closed" ? "closed"
    : c.status === "Waiting" ? "waiting"
    : c.status === "Working" ? "working"
    : "new";
  const priority: CasePriority =
    c.priority === "High" ? "high"
    : c.priority === "Medium" ? "medium"
    : "low";
  return {
    id: c.id,
    kind: "case",
    title: c.record_title,
    ref: c.reference_number,
    status,
    priority,
    meta: c.scheduled_end ? `Due ${formatRelative(c.scheduled_end)}` : undefined,
    updatedAt: c.updated_at ?? undefined,
    emoji: c.emoji,
    scheduledStart: c.scheduled_start ?? undefined,
    scheduledEnd: c.scheduled_end ?? undefined,
    actualStart: c.actual_start ?? undefined,
    actualEnd: c.actual_end ?? undefined,
    aiSummary: c.ai_summary ?? undefined,
    remarks: c.remarks ?? undefined,
  };
}

function taskFromGql(t: GqlTask): TaskEntity {
  const overdue = !!t.due && new Date(t.due).getTime() < Date.now() && !t.completed;
  return {
    id: t.id,
    kind: "task",
    title: t.record_title,
    meta: t.due ? (overdue ? `Overdue ${formatRelative(t.due)}` : `Due ${formatRelative(t.due)}`) : undefined,
    overdue,
    done: !!t.completed,
    due: t.due ?? undefined,
    updatedAt: t.updated_at ?? undefined,
    emoji: t.emoji,
  };
}

function noteFromGql(n: GqlNote): NoteEntity {
  return {
    id: n.id,
    kind: "note",
    title: n.record_title,
    updatedAt: n.updated_at ?? undefined,
    emoji: n.emoji,
  };
}

function addressFromGql(a: GqlAddress): AddressEntity {
  return {
    id: a.id,
    kind: "address",
    title: a.record_title,
    ref: a.reference_number,
    meta: undefined,
    subtitle: [a.city, a.state, a.postal_code].filter(Boolean).join(", "),
    emoji: a.emoji,
  };
}

function carFromGql(c: GqlCar): CarEntity {
  return {
    id: c.id,
    kind: "car",
    title: c.record_title,
    vin: c.id,
    meta: [c.year, c.make, c.model].filter(Boolean).join(" "),
    emoji: c.emoji,
  };
}

function carRentalFromGql(r: GqlCarRental): CarRentalEntity {
  const carDescription = r.car_make_model || r.car_class_given || undefined;
  // Meta line: rental company name, else the make/model so the row
  // always has a useful secondary line.
  const meta = r.business?.name ?? carDescription ?? undefined;
  return {
    id: r.id,
    kind: "rental",
    title: r.record_title,
    ref: r.reference_number,
    meta,
    emoji: r.emoji,
    company: r.business?.name ?? undefined,
    carDescription,
  };
}

function assetFromGql(a: GqlAsset): AssetEntity {
  return {
    id: a.id,
    kind: "asset",
    title: a.record_title,
    ref: a.reference_number,
    meta: a.serial_number ?? undefined,
    emoji: a.emoji,
  };
}

function trackingFromGql(t: GqlTrackingNumber): TrackingEntity {
  const carrier = t.courier?.name?.toUpperCase();
  const carrierEnum =
    carrier === "USPS" || carrier === "UPS" || carrier === "FEDEX" || carrier === "DHL"
      ? (carrier as TrackingEntity["carrier"])
      : undefined;
  return {
    id: t.id,
    kind: "tracking",
    title: t.record_title,
    carrier: carrierEnum,
    meta: t.courier?.name ?? undefined,
    emoji: t.emoji,
  };
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.floor((then - now) / 86_400_000);
  if (days < -1) return `${-days}d ago`;
  if (days === -1) return "yesterday";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 7) return `in ${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
